<script>
	// NPM Modules
	import { createEventDispatcher, tick } from 'svelte';
	import { collect } from 'collect.js';
	// Stores
	import MarketStore from '@/Stores/Sport/MarketStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	import SportEventStore from '@/Stores/Sport/SportEventStore';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import Icon from '@/Components/Icon.svelte';
	import PillTextInput from '@/Components/PillTextInput.svelte';
	import Switch from '@/Components/Switch.svelte';
	import TextAreaInput from '@/Components/TextAreaInput.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	// Models
	import MarketGroupSet from '@/Models/MarketGroupSet';
	// Constants
	import { Status as MarketStatus } from '@/Constants/Market.js';
	import {
		SportRollingTypeIdentifierDisplayNames,
		SportRollingTypeIdentifierNames,
		SportRollingTypeIdentifiers,
		SportRollingTypes
	} from '@/Constants/Sport';

	export let event;
	export let open;

	const dispatch = createEventDispatcher();

	let sending = false;
	let errors = null;
	let parentMarketsLoading = false;
	let marketGroupSetNotFound = false;
	let createRollingMarket = false;
	let parentMarketSelector = null;
	let parentMarkets = [];
	let marketsToAdd = [];
	let marketIdsToAdd = [];
	let marketForm = {
		event_id: event.id,
		parent_market_id: null,
		name: '',
		status: MarketStatus.Open,
		line: 0,
		sport_selections: [
			{
				id: 1,
				name: '',
				price: '1.00'
			},
			{
				id: 2,
				name: '',
				price: '1.00'
			}
		]
	};
	let onlyShowManualParentMarkets = true;
	let selectionString = '';

	$: selectionsCount = marketForm && marketForm.sport_selections.length;
	$: filteredParentMarkets =
		parentMarkets &&
		(onlyShowManualParentMarkets || !onlyShowManualParentMarkets) &&
		(createRollingMarket || !createRollingMarket) &&
		filterParentMarkets(parentMarkets);
	$: createRollingMarket &&
		marketsToAdd &&
		marketsToAdd.length > 0 &&
		setupRollingMarketSelections(marketsToAdd[0].rolling_type);
	$: hasDraw = createRollingMarket && marketForm && marketForm.sport_selections && selectionHasDraw();

	async function getParentMarkets() {
		parentMarketsLoading = true;
		await CompetitionStore.getCompetitionsForSport(event.sport.id);
		let competition = $CompetitionStore.firstWhere('id', event.competition.id);

		let availableParentMarkets = [];

		let marketGroupSet = new MarketGroupSet();
		if (competition.market_group_set.id) {
			await marketGroupSet
				.with('market_set_groups.grouped_parent_markets')
				.find(competition.market_group_set.id)
				.then((marketGroupSet) => {
					marketGroupSet.market_set_groups.data.each((marketSetGroup) => {
						marketSetGroup.grouped_parent_markets.data.each((groupedParentMarkets) => {
							availableParentMarkets.push({
								id: groupedParentMarkets.parent_market_id,
								name: groupedParentMarkets.name,
								group: marketSetGroup.name,
								type: groupedParentMarkets.market_type.name,
								manual: groupedParentMarkets.is_manual,
								rolling: !!groupedParentMarkets.market_type.rolling_type,
								rolling_type:
									groupedParentMarkets.market_type.rolling_type && groupedParentMarkets.market_type.rolling_type.id
							});
						});
					});
				});
			availableParentMarkets = collect(availableParentMarkets).unique('id').all();

			let usedParentMarkets = [];
			await SportEventStore.getEventMarketGroups(event.id);
			$MarketStore.where('event_id', event.id).each((market) => {
				usedParentMarkets.push(market.parent_market_id);
			});

			parentMarkets = collect(availableParentMarkets)
				.filter((parentMarket) => {
					return !usedParentMarkets.includes(parentMarket.id);
				})
				.all();
		} else {
			marketGroupSetNotFound = true;
		}
		parentMarketsLoading = false;
	}

	getParentMarkets();

	function resetPage() {
		marketForm = {
			event_id: event.id,
			parent_market_id: null,
			name: '',
			status: MarketStatus.Open,
			line: 0,
			sport_selections: []
		};
		marketsToAdd = [];
		marketIdsToAdd = [];
		tick().then(() => {
			marketForm.sport_selections = [
				{
					id: 1,
					name: '',
					price: '1.00'
				},
				{
					id: 2,
					name: '',
					price: '1.00'
				}
			];
		});
	}

	function createMarket() {
		if (marketsToAdd.length === 0) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'You need to add a market to create first!',
				timeout: 3
			});
			return;
		}
		let baseMarketData = {
			event_id: parseInt(event.id),
			status_id: parseInt(marketForm.status.toString()),
			sport_selections: []
		};
		if (createRollingMarket) {
			if (marketForm.line === 0) {
				errors = {};
				errors['line'] = ['Line must be greater than 0'];
				return false;
			}
			baseMarketData['line'] = parseFloat(marketForm.line);
		}
		for (const selection in marketForm.sport_selections) {
			if (marketForm.sport_selections[selection].price < 1) {
				errors = {};
				errors['sport_selections.' + selection + '.price'] = ['Odds must be greater than or equal too $1.00'];
				return false;
			}
			//ROLLING MARKETS - transform the UI data to api request data
			if (createRollingMarket) {
				if (marketForm.sport_selections[selection].line === 0) {
					errors = {};
					errors['sport_selections.' + selection + '.line'] = ['Selections line must be greater than 0'];
					return false;
				}

				if (marketsToAdd[0].rolling_type === SportRollingTypes.HANDICAP) {
					baseMarketData.sport_selections.push({
						name: marketForm.sport_selections[selection].name,
						price: marketForm.sport_selections[selection].price * 100,
						line:
							marketForm.sport_selections[selection].identifier === SportRollingTypeIdentifiers.POSITIVE
								? parseFloat(marketForm.sport_selections[selection].line)
								: marketForm.sport_selections[selection].line * -1
					});
				} else if (marketsToAdd[0].rolling_type === SportRollingTypes.OVER_UNDER) {
					baseMarketData.sport_selections.push({
						name: marketForm.sport_selections[selection].name,
						line: parseFloat(marketForm.sport_selections[selection].line),
						price: marketForm.sport_selections[selection].price * 100,
						identifier: SportRollingTypeIdentifierNames[marketForm.sport_selections[selection].identifier]
					});
				} else if (marketsToAdd[0].rolling_type === SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER) {
					if (marketForm.sport_selections[selection].linked) {
						for (const selectionSearch of marketForm.sport_selections) {
							if (selectionSearch.id === marketForm.sport_selections[selection].linked) {
								marketForm.sport_selections[selection].name = selectionSearch.name;
							}
						}
					}
					baseMarketData.sport_selections.push({
						name: `${marketForm.sport_selections[selection].name}/${
							marketForm.sport_selections[selection].identifier === SportRollingTypeIdentifiers.DRAW_UNDER ||
							marketForm.sport_selections[selection].identifier === SportRollingTypeIdentifiers.DRAW_OVER
								? marketForm.sport_selections[selection].identifier === SportRollingTypeIdentifiers.DRAW_UNDER
									? SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.UNDER]
									: SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.OVER]
								: SportRollingTypeIdentifierDisplayNames[marketForm.sport_selections[selection].identifier]
						}`,
						line: parseFloat(marketForm.sport_selections[selection].line),
						price: marketForm.sport_selections[selection].price * 100,
						identifier: SportRollingTypeIdentifierNames[marketForm.sport_selections[selection].identifier]
					});
				}
			} else {
				baseMarketData.sport_selections.push({
					name: marketForm.sport_selections[selection].name,
					price: marketForm.sport_selections[selection].price * 100
				});
			}
		}
		let marketsToCreate = [];
		for (const market of marketsToAdd) {
			let marketData = JSON.parse(JSON.stringify(baseMarketData));
			marketData['name'] = market.name;
			marketData['parent_market_id'] = market.id;

			const marketToBeAdded = new Promise((resolve, reject) => {
				MarketStore.createManualMarket(marketData)
					.then(() => {
						resolve();
					})
					.catch((error) => {
						reject(error);
					});
			});
			marketsToCreate.push(marketToBeAdded);
		}
		sending = true;
		Promise.all(marketsToCreate)
			.then(() => {
				notify({
					type: 'success',
					title: 'Manual market created',
					message: `Manual market successfully created for ${event.name}!`,
					timeout: 3
				});
				dispatch('created');
				open = false;
				marketForm = {
					event_id: event.id,
					parent_market_id: null,
					status: MarketStatus.Open,
					name: '',
					sport_selections: []
				};
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem creating manual market',
					timeout: 3
				});
			})
			.finally(() => {
				sending = false;
			});
	}

	function addSelectionToMarket(name = '', price = '1.00') {
		marketForm.sport_selections = [
			...marketForm.sport_selections,
			{
				id: marketForm.sport_selections.length + 1,
				name: name,
				price: price
			}
		];
	}

	function setSelectionToMarket(value) {
		if (value === marketForm.sport_selections.length) {
			return;
		}

		if (value > marketForm.sport_selections.length) {
			let numberToCreate = value - marketForm.sport_selections.length;
			for (let i = 0; i < numberToCreate; i++) {
				marketForm.sport_selections.push({
					id: marketForm.sport_selections.length + 1,
					name: '',
					price: '1.00'
				});
			}
			marketForm.sport_selections = marketForm.sport_selections;
		}
	}

	function removeSelectionFromMarket(key) {
		marketForm.sport_selections.splice(key, 1);

		let count = 1;
		for (let index in marketForm.sport_selections) {
			marketForm.sport_selections[index].id = count;
			count = count + 1;
		}

		marketForm.sport_selections = marketForm.sport_selections; //Trigger reactivity
	}

	function filterParentMarkets(parentMarketsToBeFiltered) {
		let parentMarketsToFilter = collect(parentMarketsToBeFiltered);

		if (onlyShowManualParentMarkets) {
			parentMarketsToFilter = parentMarketsToFilter.where('manual', true);
		}

		parentMarketsToFilter = parentMarketsToFilter.where('rolling', createRollingMarket);

		return parentMarketsToFilter.all();
	}

	function removeMarketFromAddList(market) {
		let indexToRemove;
		for (const index in marketsToAdd) {
			if (marketsToAdd[index].id === market.id) {
				indexToRemove = index;
				break;
			}
		}
		marketsToAdd.splice(indexToRemove, 1);
		for (const index in marketIdsToAdd) {
			if (marketIdsToAdd[index] === market.id) {
				indexToRemove = index;
				break;
			}
		}
		marketIdsToAdd.splice(indexToRemove, 1);
		marketsToAdd = marketsToAdd;
		marketIdsToAdd = marketIdsToAdd;
	}

	function createSelectionsFromString(stringSent) {
		// eslint-disable-next-line no-useless-escape
		const regexp = /(.*)[\s]([\d\.]*)(?:[\r\n]|$)/g;
		const selectionArray = [...stringSent.matchAll(regexp)];
		let selectionsToAdd = [];
		for (let index in selectionArray) {
			selectionsToAdd.push({
				name: selectionArray[index][1],
				price: selectionArray[index][2]
			});
		}

		let canAddToExisting = false;
		if (marketForm.sport_selections.length > 0) {
			canAddToExisting = true;
		}
		for (const selection of selectionsToAdd) {
			if (canAddToExisting) {
				let added = false;
				for (let index in marketForm.sport_selections) {
					if (marketForm.sport_selections[index].name === '' && marketForm.sport_selections[index].price === '1.00') {
						marketForm.sport_selections[index].name = selection.name;
						marketForm.sport_selections[index].price = selection.price;
						added = true;
						break;
					}
				}
				if (!added) {
					canAddToExisting = false;
				} else {
					continue;
				}
			}
			addSelectionToMarket(selection.name, selection.price);
		}
	}

	function setupRollingMarketSelections(rollingTypeId) {
		if (rollingTypeId === SportRollingTypes.HANDICAP) {
			marketForm.sport_selections = [
				{
					id: 1,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.POSITIVE,
					price: '1.00'
				},
				{
					id: 2,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.NEGATIVE,
					price: '1.00'
				}
			];
		} else if (rollingTypeId === SportRollingTypes.OVER_UNDER) {
			marketForm.sport_selections = [
				{
					id: 1,
					name: SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.OVER],
					line: 0,
					identifier: SportRollingTypeIdentifiers.OVER,
					price: '1.00'
				},
				{
					id: 2,
					name: SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.UNDER],
					line: 0,
					identifier: SportRollingTypeIdentifiers.UNDER,
					price: '1.00'
				}
			];
		} else if (rollingTypeId === SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER) {
			marketForm.sport_selections = [
				{
					id: 1,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.OVER,
					price: '1.00'
				},
				{
					id: 2,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.UNDER,
					price: '1.00',
					linked: 1
				},
				{
					id: 3,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.OVER,
					price: '1.00'
				},
				{
					id: 4,
					name: '',
					line: 0,
					identifier: SportRollingTypeIdentifiers.UNDER,
					price: '1.00',
					linked: 3
				}
			];
		}
	}

	function updateLineValue(marketLine) {
		for (let index in marketForm.sport_selections) {
			marketForm.sport_selections[index].line = marketLine;
		}
	}

	function SwapIdentifier(currentIdentifier) {
		for (let index in marketForm.sport_selections) {
			if (
				marketForm.sport_selections[index].identifier === currentIdentifier &&
				(marketsToAdd[0].rolling_type === SportRollingTypes.HANDICAP ||
					marketsToAdd[0].rolling_type === SportRollingTypes.OVER_UNDER)
			) {
				if (currentIdentifier === SportRollingTypeIdentifiers.POSITIVE) {
					marketForm.sport_selections[index].identifier = SportRollingTypeIdentifiers.NEGATIVE;
				} else if (currentIdentifier === SportRollingTypeIdentifiers.NEGATIVE) {
					marketForm.sport_selections[index].identifier = SportRollingTypeIdentifiers.POSITIVE;
				} else if (currentIdentifier === SportRollingTypeIdentifiers.DRAW_OVER) {
					marketForm.sport_selections[index].identifier = SportRollingTypeIdentifiers.DRAW_UNDER;
				} else if (currentIdentifier === SportRollingTypeIdentifiers.DRAW_UNDER) {
					marketForm.sport_selections[index].identifier = SportRollingTypeIdentifiers.DRAW_OVER;
				}
			} else if (
				currentIdentifier !== SportRollingTypeIdentifiers.DRAW_OVER &&
				currentIdentifier !== SportRollingTypeIdentifiers.DRAW_UNDER &&
				marketForm.sport_selections[index].identifier !== SportRollingTypeIdentifiers.DRAW_OVER &&
				marketForm.sport_selections[index].identifier !== SportRollingTypeIdentifiers.DRAW_UNDER &&
				marketsToAdd[0].rolling_type === SportRollingTypes.HANDICAP
			) {
				marketForm.sport_selections[index].identifier = currentIdentifier;
			}
			if (marketsToAdd[0].rolling_type !== SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER) {
				if (marketForm.sport_selections[index].identifier === SportRollingTypeIdentifiers.OVER) {
					marketForm.sport_selections[index].name =
						SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.OVER];
				} else if (marketForm.sport_selections[index].identifier === SportRollingTypeIdentifiers.UNDER) {
					marketForm.sport_selections[index].name =
						SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.UNDER];
				}
			}
		}
	}

	function AddDrawSelection() {
		if (marketsToAdd[0].rolling_type === SportRollingTypes.OVER_UNDER) {
			for (const index in marketForm.sport_selections) {
				if (marketForm.sport_selections[index].id === 2) {
					marketForm.sport_selections.push({
						id: 3,
						name: marketForm.sport_selections[index].name,
						line: marketForm.sport_selections[index].line,
						identifier: marketForm.sport_selections[index].identifier,
						price: marketForm.sport_selections[index].price
					});
					marketForm.sport_selections[index].name =
						SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.DRAW_OVER];
					marketForm.sport_selections[index].line = marketForm.line;
					marketForm.sport_selections[index].identifier = SportRollingTypeIdentifiers.DRAW_OVER;
					marketForm.sport_selections[index].price = '1.00';
				}
			}
		} else if (marketsToAdd[0].rolling_type === SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER) {
			for (const index in marketForm.sport_selections) {
				if ([3, 4].includes(marketForm.sport_selections[index].id)) {
					marketForm.sport_selections.push(
						marketForm.sport_selections[index].linked
							? {
									id: marketForm.sport_selections[index].id + 2,
									name: marketForm.sport_selections[index].name,
									line: marketForm.sport_selections[index].line,
									identifier: marketForm.sport_selections[index].identifier,
									price: marketForm.sport_selections[index].price,
									linked: marketForm.sport_selections[index].linked + 2
								}
							: {
									id: marketForm.sport_selections[index].id + 2,
									name: marketForm.sport_selections[index].name,
									line: marketForm.sport_selections[index].line,
									identifier: marketForm.sport_selections[index].identifier,
									price: marketForm.sport_selections[index].price
								}
					);
					marketForm.sport_selections[index].name =
						marketForm.sport_selections[index].id === 3
							? SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.DRAW_OVER]
							: SportRollingTypeIdentifierDisplayNames[SportRollingTypeIdentifiers.DRAW_UNDER];
					marketForm.sport_selections[index].line = marketForm.line;
					marketForm.sport_selections[index].identifier =
						marketForm.sport_selections[index].id === 3
							? SportRollingTypeIdentifiers.DRAW_OVER
							: SportRollingTypeIdentifiers.DRAW_UNDER;
					marketForm.sport_selections[index].price = '1.00';
					if (marketForm.sport_selections[index].id === 4) {
						marketForm.sport_selections[index].linked = 3;
					}
				}
			}
		}

		marketForm.sport_selections = marketForm.sport_selections; //trick reactivity
	}

	function selectionHasDraw() {
		for (let index in marketForm.sport_selections) {
			if (
				marketForm.sport_selections[index].identifier === SportRollingTypeIdentifiers.DRAW_OVER ||
				marketForm.sport_selections[index].identifier === SportRollingTypeIdentifiers.DRAW_UNDER
			) {
				return true;
			}
		}
		return false;
	}

	function getRollingButtonTitle(rollingType) {
		if (rollingType == SportRollingTypeIdentifiers.POSITIVE) {
			return 'Positive (Click to swap)';
		} else if (rollingType == SportRollingTypeIdentifiers.OVER) {
			return 'Over (Click to swap)';
		} else if (rollingType == SportRollingTypeIdentifiers.NEGATIVE) {
			return 'Negative (Click to swap)';
		} else if (rollingType == SportRollingTypeIdentifiers.UNDER) {
			return 'Under (Click to swap)';
		} else if (rollingType == SportRollingTypeIdentifiers.DRAW_OVER) {
			return 'Draw Over (Click to swap)';
		} else if (rollingType == SportRollingTypeIdentifiers.DRAW_UNDER) {
			return 'Draw Under (Click to swap)';
		}
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)} className="md:w-2/3 lg:w-1/2 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Create market for {event.name}</h2>
		<div class="flex flex-col w-full px-4 pt-6 text-left">
			<div class="flex flex-row justify-center items-center">
				<div class="btn-group">
					<button
						class="btn {!createRollingMarket ? 'btn-primary-action' : 'btn-muted text-dark'}"
						on:click={() => {
							resetPage();
							createRollingMarket = false;
						}}
					>
						Standard Market
					</button>
					<button
						class="btn {createRollingMarket ? 'btn-primary-action' : 'btn-muted text-dark'}"
						on:click={() => {
							resetPage();
							createRollingMarket = true;
						}}
					>
						Rolling Market
					</button>
				</div>
			</div>

			<div class="flex flex-row">
				<Switch
					name="onlyShowManualMarkets"
					on:change={() => {
						onlyShowManualParentMarkets = !onlyShowManualParentMarkets;
					}}
					checked={onlyShowManualParentMarkets}
					className="-mt-1"
				/>
				<div class="ml-3">Only show manual markets</div>
			</div>
			<SelectInput
				label="Select Market to add"
				className="py-4 w-full text-left"
				errors={errors && errors.parent_market_id}
				name="marketParentMarketId"
				disabled={parentMarketsLoading || sending}
				required={true}
				bind:value={parentMarketSelector}
				on:change={(event) => {
					const parentMarketToAdd = parseInt(event.target.value);
					if (parentMarketToAdd) {
						if (createRollingMarket) {
							marketsToAdd = [];
							marketIdsToAdd = [];
						}
						marketsToAdd.push(collect(parentMarkets).firstWhere('id', parentMarketToAdd));
						marketIdsToAdd.push(parentMarketToAdd);
						marketsToAdd = marketsToAdd; //trick reactivity
						marketIdsToAdd = marketIdsToAdd; //trick reactivity
						if (!createRollingMarket) {
							tick().then(() => {
								parentMarketSelector = null;
							});
						}
					}
				}}
			>
				{#if parentMarketsLoading}
					<option value={null}>Loading available markets...</option>
				{:else if !parentMarketsLoading}
					<option value={null}>No markets available</option>
				{:else if marketGroupSetNotFound}
					<option value={null}>No market group set found...</option>
				{:else}
					<option value={null}>Please select a market to create</option>
				{/if}
				{#each collect(filteredParentMarkets).unique('group').all() as { group } (group)}
					<optgroup label={group}>
						{#each collect(filteredParentMarkets)
							.filter((parentMarket) => {
								return parentMarket.group === group;
							})
							.sortBy('name')
							.all() as parentMarket (parentMarket.id)}
							<option value={parentMarket.id} disabled={marketIdsToAdd.includes(parentMarket.id)}
								>{parentMarket.name}: [{parentMarket.type}]
							</option>
						{/each}
					</optgroup>
				{/each}
			</SelectInput>
			{#if !createRollingMarket}
				<h1>Markets being added</h1>
				<div class="flex justify-center mt-2 flex-wrap">
					{#each marketsToAdd as market (market.id)}
						<PillTextInput
							action="remove"
							name={market.id}
							disabled={sending}
							bgColour="bg-indigo-500"
							className="m-2"
							label={market.name}
							value={false}
							on:remove={() => removeMarketFromAddList(market)}
						/>
					{:else}
						<div class="font-semibold my-2">No markets added yet</div>
					{/each}
				</div>
			{/if}
			<SelectInput
				label="Market initial status (Default: Open)"
				className="py-4 w-full text-left"
				errors={errors && errors.status_id}
				name="marketInitialStatus"
				disabled={parentMarketsLoading || sending}
				required={true}
				bind:value={marketForm.status}
			>
				<option value={MarketStatus.Open}>Open</option>
				<option value={MarketStatus.Suspended}>Suspended</option>
			</SelectInput>
			{#if !createRollingMarket}
				<div class="mt-4">
					<div class="flex flex-row items-center">
						<div class="mr-4">Selections to add:</div>
						<div class="flex flex-row border border-gray-400">
							<input
								on:change={(event) => setSelectionToMarket(event.target.value)}
								on:keyup={(event) => {
									if (event.keyCode === 13) {
										setSelectionToMarket(event.target.value);
									}
								}}
								disabled={sending}
								type="number"
								class="w-10 text-xs text-center border-gray-400 focus:outline-none"
								value={selectionsCount}
							/>
							<button
								disabled={sending}
								on:click|stopPropagation={() => addSelectionToMarket()}
								class="p-1 font-semibold text-white bg-gray-500 cursor-pointer focus:outline-none hover:bg-gray-400"
							>
								<span class="m-auto">+</span>
							</button>
						</div>
					</div>
					<div class="font-light text-sm mt-1">These selections will be added to every selected market.</div>
					<div class="flex flex-row items-center justify-center">
						<TextAreaInput
							rows="4"
							disabled={sending}
							name="pasteFromString"
							className="py-4 w-1/2 text-left"
							placeholder="Paste selection text here."
							bind:value={selectionString}
						/>
						<LoadingButton
							on:click={() => createSelectionsFromString(selectionString)}
							loading={false}
							disabled={sending}
							className="ml-2 my-4 btn btn-primary"
						>
							Generate selections
						</LoadingButton>
					</div>
				</div>
			{/if}
			{#if createRollingMarket}
				<div class="">
					<label class="form-label" for="marketLine">Market Line Value</label>
					<input
						id="marketLine"
						name="marketLine"
						required={true}
						disabled={sending}
						type="number"
						class="form-input focus:outline-none"
						class:border-orange-400={parseFloat(marketForm.line) === 0.0}
						class:error={errors && errors.line}
						min="0"
						step="0.1"
						tabindex="0"
						placeholder="Enter Market Line Value"
						on:change={(event) => {
							marketForm.line = parseFloat(event.target.value).toFixed(1);
							updateLineValue(marketForm.line);
						}}
						on:keyup={(event) => {
							if (event.keyCode === 13) {
								marketForm.line = parseFloat(event.target.value).toFixed(1);
								updateLineValue(marketForm.line);
							}
						}}
						value={marketForm.line}
					/>

					{#if errors && errors.line}
						<div class="form-error">{errors.line}</div>
					{/if}
				</div>

				{#if marketsToAdd.length > 0}
					{#if [SportRollingTypes.OVER_UNDER, SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER].includes(marketsToAdd[0].rolling_type) && !hasDraw}
						<div class="flex justify-center items-center mt-2 w-full">
							<button
								disabled={sending}
								tabindex="-1"
								title="Click to add a draw selection"
								class="px-2.5 pt-1.5 pb-2.5 mx-1 text-white bg-success rounded hover:bg-grey focus:outline-none flex items-center justify-center"
								on:click={AddDrawSelection}
							>
								Add a draw selection
							</button>
						</div>
					{/if}
				{/if}
			{/if}
			{#if !createRollingMarket || marketsToAdd.length > 0}
				<div class="flex justify-center">
					<table class="text-left mt-2 w-2/3">
						{#if marketForm.sport_selections.length > 0}
							{#if createRollingMarket}
								<tr>
									<th class="w-pw" />
									<th class="w-1/2">Selection Name</th>
									<th class="text-center">Type</th>
									<th class="text-center w-16">Line</th>
									<th class="">Odds ($)</th>
									<th class="w-px" />
								</tr>
							{:else}
								<tr>
									<th class="w-pw" />
									<th class="w-3/4">Selection Name</th>
									<th class="w-1/4">Odds ($)</th>
									<th class="w-px" />
								</tr>
							{/if}
						{/if}
						{#each marketForm.sport_selections as selection, key (key)}
							<tr class:opacity-60={sending}>
								<td>
									<span class="mr-2">{selection.id}</span>
								</td>
								{#key selection.linked}
									{#if !selection.linked}
										<td
											rowspan={marketsToAdd &&
											marketsToAdd[0] &&
											marketsToAdd[0]?.rolling_type === SportRollingTypes.MATCH_WINNER_AND_OVER_UNDER
												? 2
												: 1}
										>
											<TextInput
												name={`selection.${selection.id}.name`}
												required={true}
												disabled={sending ||
													(marketsToAdd &&
														marketsToAdd[0] &&
														marketsToAdd[0]?.rolling_type === SportRollingTypes.OVER_UNDER)}
												className="w-full text-left"
												tabindex="-1"
												errors={errors && errors['sport_selections.' + key + '.name']}
												placeholder="Enter selection name"
												bind:value={selection.name}
												on:focus={(event) => event.target.select()}
												on:keydown={(event) => {
													if (event.keyCode == 9) {
														event.preventDefault();
														event.stopPropagation();
														if (key === marketForm.sport_selections.length - 1) {
															document.getElementById(`selection.${marketForm.sport_selections[0].id}.price`).focus();
														} else {
															if (marketForm.sport_selections[key + 1].linked) {
																if (key + 1 === marketForm.sport_selections.length - 1) {
																	document
																		.getElementById(`selection.${marketForm.sport_selections[0].id}.price`)
																		.focus();
																} else {
																	document
																		.getElementById(`selection.${marketForm.sport_selections[key + 2].id}.name`)
																		.focus();
																}
															} else {
																document
																	.getElementById(`selection.${marketForm.sport_selections[key + 1].id}.name`)
																	.focus();
															}
														}
														return false;
													}
												}}
											/>
										</td>
									{/if}
								{/key}
								{#if createRollingMarket}
									<td>
										<button
											disabled={sending}
											tabindex="-1"
											title={getRollingButtonTitle(selection.identifier)}
											class="px-2.5 pt-1.5 pb-2.5 mx-1 text-white rounded hover:bg-grey focus:outline-none flex items-center justify-center w-9"
											class:bg-success={[
												SportRollingTypeIdentifiers.POSITIVE,
												SportRollingTypeIdentifiers.OVER
											].includes(selection.identifier)}
											class:bg-danger={[
												SportRollingTypeIdentifiers.NEGATIVE,
												SportRollingTypeIdentifiers.UNDER
											].includes(selection.identifier)}
											class:bg-orange-500={[
												SportRollingTypeIdentifiers.DRAW_OVER,
												SportRollingTypeIdentifiers.DRAW_UNDER
											].includes(selection.identifier)}
											on:click={() => SwapIdentifier(selection.identifier)}
										>
											{#if selection.identifier == SportRollingTypeIdentifiers.POSITIVE}
												<span class="icon-q font-extrabold text-xl flex items-center justify-center">+</span>
											{:else if selection.identifier == SportRollingTypeIdentifiers.NEGATIVE}
												<span class="icon-q font-extrabold text-xl flex items-center justify-center">-</span>
											{:else if [SportRollingTypeIdentifiers.OVER, SportRollingTypeIdentifiers.DRAW_OVER].includes(selection.identifier)}
												<Icon name="chevron-up" />
											{:else if [SportRollingTypeIdentifiers.UNDER, SportRollingTypeIdentifiers.DRAW_UNDER].includes(selection.identifier)}
												<Icon name="chevron-down" />
											{/if}
										</button>
									</td>
									<td>
										<div class="w-full text-left">
											<input
												name={`selection.${selection.id}.line`}
												required={true}
												type="number"
												class="form-input focus:outline-none"
												class:error={errors && errors['sport_selections.' + key + '.line']}
												step="0.01"
												tabindex="-1"
												disabled={true}
												placeholder="Enter selection line"
												value={selection.line}
											/>

											{#if errors && errors['sport_selections.' + key + '.line']}
												<div class="form-error">{errors['sport_selections.' + key + '.line'][0]}</div>
											{/if}
										</div>
									</td>
								{/if}
								<td>
									<div class="w-full text-left">
										<input
											id={`selection.${selection.id}.price`}
											name={`selection.${selection.id}.price`}
											required={true}
											disabled={sending}
											type="number"
											class="form-input focus:outline-none"
											class:border-orange-400={parseFloat(selection.price) === 1.0}
											class:error={errors && errors['sport_selections.' + key + '.price']}
											min="1.00"
											step="0.01"
											tabindex="-1"
											placeholder="Enter selection odds"
											on:change={(event) => (selection.price = parseFloat(event.target.value).toFixed(2))}
											on:focus={(event) => event.target.select()}
											on:keyup={(event) => {
												if (event.keyCode === 13) {
													//enter was pressed
													selection.price = parseFloat(event.target.value).toFixed(2);
													if (key === marketForm.sport_selections.length - 1) {
														//last price on the form
														tick().then(() => {
															createMarket();
														});
													}
												}
											}}
											on:keydown={(event) => {
												if (event.keyCode == 9) {
													event.preventDefault();
													event.stopPropagation();
													if (key === marketForm.sport_selections.length - 1) {
														document.getElementById(`selection.${marketForm.sport_selections[0].id}.name`).focus();
													} else {
														document.getElementById(`selection.${selection.id + 1}.price`).focus();
													}
													return false;
												}
											}}
											value={selection.price}
										/>

										{#if errors && errors['sport_selections.' + key + '.price']}
											<div class="form-error">{errors['sport_selections.' + key + '.price'][0]}</div>
										{/if}
									</div>
								</td>
								<td>
									{#if !createRollingMarket}
										<button
											disabled={sending}
											on:click={() => removeSelectionFromMarket(key)}
											tabindex="-1"
											title="Remove selection from market"
											class="p-1.5 ml-1 text-white rounded bg-danger hover:bg-grey focus:outline-none"
											class:opacity-60={sending}
										>
											<Icon name="bin" />
										</button>
									{/if}
								</td>
							</tr>
						{:else}
							<tr class="text-center">
								<td colspan="2">No selections added</td>
							</tr>
						{/each}
					</table>
				</div>
			{/if}

			{#if errors && errors.sport_selections}
				<div class="form-error">{errors.sport_selections[0]}</div>
			{/if}

			<LoadingButton on:click={() => createMarket()} loading={sending} className="my-4 btn btn-primary">
				{sending ? 'Creating' : 'Create'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
